import { useEffect, useRef, useState } from "react";
import type { GroupInfo, MaterialKind } from "../../../shared/api-contract";
import {
  defaultIndexOf,
  effectiveConfigurator,
  matchGroupTargets,
  slotKindOf,
  type ConfiguratorOption,
  type ConfiguratorOptionGroup,
  type OptionKind,
} from "../../../features/vault/configurator";
import { useToast } from "../../../shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { loadKindOf } from "../../../state/editor/modelLoads";
import { notice, useDomainState, useUi } from "../../../state/editor/useEditor";
import { CustomerBar } from "../../configurator/CustomerBar";
import { Icon } from "../../kit";
import type { BasicEngine, HistoryMark } from "../basic/applyEngine";
import { markKind } from "../basic/markKind";
import { useBasicEngine } from "../basic/useBasicEngine";
import { CARD_GAP } from "../layout";
import { useConfiguratorDraft } from "./draft";
import { optionMark } from "./optionMark";
import { registerPreviewExit } from "./previewExit";
import { previewOption } from "./studioApply";

/**
 * Preview as customer — the studio steps aside and the piece is shown the way
 * a shopper gets it on the shared link: the same glass bar (`CustomerBar`, the
 * link's own component), the same name and running price, the same parts in
 * the same order, opening on the same choices.
 *
 * Every choice a "shopper" clicks here goes through the studio's ordinary
 * apply (`previewOption`, one engine step each). On the way in the engine's
 * history is bookmarked (`markHistory`); Exit and Esc rewind to it
 * (`rewindTo`) — each step taken since is undone, newest first, and Undo and
 * Redo are put back exactly as they were, so the design comes back as it was
 * and the preview leaves nothing in the designer's history.
 *
 * Toggled by the session-only `ui.customerPreview` flag. Like Present, the
 * chrome hides by visibility only — `main` and the iframe never move.
 */

/** A def group shown to the shopper, against the live part it targets. */
interface PreviewGroup extends ConfiguratorOptionGroup {
  target: GroupInfo;
  slotKind: OptionKind;
}

/** Is the part already wearing exactly this choice (nothing to apply)? */
function wears(engine: BasicEngine, part: GroupInfo, option: ConfiguratorOption): boolean {
  const want = optionMark(option);
  const have = engine.presetFor(part.id);
  if (!want || !have) return false;
  return (
    want.url === have.url &&
    want.presetId === have.presetId &&
    (want.finishId ?? null) === (have.finishId ?? null) &&
    markKind(want, part.kind as MaterialKind) === part.kind
  );
}

/** A glTF scene name is not the piece's name ("AuxScene", "Scene"). */
const realName = (n: string | undefined) =>
  n && !/^(aux)?scene\s*\d*$|^untitled|^model$/i.test(n.trim()) ? n : undefined;

export function CustomerPreview({ modelName }: { modelName?: string }) {
  const [ui] = useUi();
  return ui.customerPreview ? <PreviewSession modelName={modelName} /> : null;
}

function PreviewSession({ modelName }: { modelName?: string }) {
  const [, patchUi] = useUi();
  const { client } = useViewer();
  const engine = useBasicEngine();
  const [cfg, draft] = useConfiguratorDraft();
  const groups = (useDomainState("groups") as GroupInfo[] | undefined) ?? [];
  const models = useDomainState("models");
  const { toast } = useToast();
  /** Which choice each def group is showing, once the shopper picks. */
  const [picked, setPicked] = useState<Record<string, number>>({});
  const [phase, setPhase] = useState<"entering" | "live" | "leaving">("entering");
  /** The history bookmark taken on the way in. */
  const markRef = useRef<Promise<HistoryMark> | null>(null);
  const leaving = useRef(false);

  // What the link shows: the same def resolution and the same part matching
  // the shared link does (`ConfiguratorView`), against this studio's parts.
  const kinds = groups.map((g) => slotKindOf(g, loadKindOf(client, g.id)));
  const def = effectiveConfigurator(cfg.def, groups.map((g, i) => ({ label: g.label, kind: kinds[i] })));
  const authored = cfg.def.groups.length > 0;
  const shown: PreviewGroup[] = matchGroupTargets(def.groups, groups)
    .filter(({ group }) => group.options.length > 0)
    .map(({ group, target, targetIndex }) => ({ ...group, target, slotKind: kinds[targetIndex] }));
  const liveGroups = shown.map((g) => ({ ...g, activeIndex: picked[g.id] ?? defaultIndexOf(g) }));
  const latest = useRef(liveGroups);
  latest.current = liveGroups;

  // In: bookmark the history, then put on each part the choice a shopper
  // opens on — as the link does for a configured piece.
  useEffect(() => {
    let alive = true;
    document.body.dataset.customerPreview = "1";
    draft.setPickerOpen(false);
    const mark = engine.markHistory();
    markRef.current = mark;
    void (async () => {
      await mark;
      if (authored) {
        for (const g of latest.current) {
          // Exit pressed meanwhile: anything enqueued after its rewind would stay.
          if (!alive || leaving.current) break;
          const opt = g.options[g.activeIndex];
          if (!opt || wears(engine, g.target, opt)) continue;
          await previewOption(engine, g.target, opt, g.slotKind).catch(() => undefined);
        }
      }
      if (alive) setPhase("live");
    })();
    return () => {
      alive = false;
      delete document.body.dataset.customerPreview;
    };
    // Once per session: the bookmark is the design as it was on the way in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exit = async () => {
    if (leaving.current) return;
    leaving.current = true;
    setPhase("leaving");
    try {
      const mark = await markRef.current;
      if (mark) await engine.rewindTo(mark);
    } catch (e) {
      notice(`Couldn't put the design back: ${e instanceof Error ? e.message : "the viewer refused it"}`);
    } finally {
      patchUi({ customerPreview: false });
    }
  };
  const exitRef = useRef(exit);
  exitRef.current = exit;
  // Opening a scene file asks the preview to step aside first (previewExit.ts).
  useEffect(() => registerPreviewExit(() => exitRef.current()), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        void exitRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pick = (groupId: string, index: number) => {
    if (leaving.current) return;
    const g = latest.current.find((x) => x.id === groupId);
    const opt = g?.options[index];
    if (!g || !opt) return;
    setPicked((p) => ({ ...p, [groupId]: index }));
    void previewOption(engine, g.target, opt, g.slotKind).catch((e: Error) => notice(`Couldn't show ${opt.name}: ${e.message}`));
  };

  const name = realName(modelName) ?? realName(models[0]?.name);
  const variants = def.variants ?? [];

  return (
    <div
      className="pointer-events-none absolute z-[120]"
      style={{ left: CARD_GAP, right: CARD_GAP, top: CARD_GAP, bottom: CARD_GAP }}
      data-customer-preview-phase={phase}
    >
      <div className="pointer-events-auto absolute left-1/2 top-5 flex max-w-[calc(100%-200px)] -translate-x-1/2 items-center gap-2 rounded-full border border-ed-border bg-ed-panel px-3.5 py-2 text-[13px] text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.08)] max-[700px]:hidden">
        <Icon name="visibility" size={17} className="shrink-0 text-ed-text-2" />
        <span className="truncate">
          <b className="font-semibold">Customer preview</b>
          <span className="text-ed-text-2"> · what shoppers see on your link</span>
        </span>
      </div>
      <button
        type="button"
        onClick={() => void exit()}
        disabled={phase === "leaving"}
        className="pointer-events-auto absolute right-5 top-5 inline-flex h-9 items-center gap-1.5 rounded-full bg-ed-ink px-4 text-[13px] font-semibold text-white shadow-[0_6px_20px_rgba(0,0,0,0.12)] hover:bg-black disabled:opacity-70"
        title="Back to the studio, with the design exactly as you left it (Esc)"
        data-customer-preview-exit
      >
        <Icon name="close" size={18} />
        {phase === "leaving" ? "Putting your design back…" : "Exit preview"}
      </button>
      {liveGroups.length > 0 ? (
        <CustomerBar
          name={name}
          groups={liveGroups}
          // The "Model" pill is drawn as the link draws it; switching the whole
          // piece here would swap the model out from under the studio.
          variants={variants}
          basePrice={def.basePrice}
          currency={def.currency ?? "USD"}
          disabled={phase === "leaving"}
          onPick={pick}
          onVariant={(vi) =>
            toast({
              tone: "info",
              title: `On your link this switches to ${variants[vi]?.label ?? "that model"}`,
              description: "The preview keeps this piece on screen.",
            })
          }
        />
      ) : (
        <p className="pointer-events-auto absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full border border-ed-border bg-ed-panel px-4 py-2 text-[13px] text-ed-text-2 shadow-[0_6px_20px_rgba(0,0,0,0.08)]">
          No choices yet — add some on the configurator bar, and shoppers can switch them here.
        </p>
      )}
    </div>
  );
}
